import React, { useCallback, useEffect } from 'react';
import { Route, Switch, useHistory } from "react-router-dom";
import AppHeader from '../AppHeader/AppHeader';
import TasksGrid from '../user/TasksGrid/TasksGrid';
import { Container, Hidden } from '@material-ui/core';
import BottomButton from '../common/BottomButton';
import { useTranslation } from 'react-i18next';
import { completedRoutes } from '../../common/constants';
import CustomBackdrop from '../common/CustomBackdrop';
import { lazyWithDelay } from '../../common/lazyWithDelay';
import Grid from '@material-ui/core/Grid';
import DesktopSideBar from '../AppHeader/DesktopSideBar/DesktopSideBar';
import * as tasksActions from '../../store/tasksSlice';
import * as regionsActions from '../../store/regionsSlice';
import * as placesActions from '../../store/placesSlice';
import { connect } from 'react-redux';
import { changeErrorPopupState } from '../../store/uiSlice';
import * as userActions from '../../store/userSlice';
import UserPayment from '../user/UserPayment/UserPayment';
import MyActivity from '../user/MyActivity/MyActivity';
import TaskInfo from '../user/TaskInfo/TaskInfo';
import PlaceInfo from '../user/PlaceInfo/PlaceInfo';
import UserPlaces from '../user/UserPlaces/UserPlaces';
import SignUpModalItem from '../SignUp/SignUpModalItem';

const ForCompanies = lazyWithDelay(import('../common/ForCompanies/ForCompanies'));
const Rules = lazyWithDelay(import('../common/Rules/Rules'));

const ContentSwitch = (props) => {
  const { desktopOption } = props;
  return (
    <Switch>
      <Route path={completedRoutes.userPayment}>
        <UserPayment />
      </Route>
      <Route path={completedRoutes.userForCompanies}>
        <React.Suspense fallback={<CustomBackdrop open />}>
          <ForCompanies />
        </React.Suspense>
      </Route>
      <Route path={completedRoutes.userRules}>
        <React.Suspense fallback={<CustomBackdrop open />}>
          <Rules />
        </React.Suspense>
      </Route>
      <Route path={completedRoutes.userActivity}>
        <React.Suspense fallback={<CustomBackdrop open />}>
          <MyActivity />
        </React.Suspense>
      </Route>
      <Route path={`${completedRoutes.taskInfoWithoutId}/:taskId`}>
        <React.Suspense fallback={<CustomBackdrop open />}>
          <TaskInfo />
        </React.Suspense>
      </Route>
      <Route path={`${completedRoutes.taskOwnerInfoWithoutId}/:placeId`}>
        <PlaceInfo />
      </Route>
      <Route path={completedRoutes.userPlaces}>
        <UserPlaces />
      </Route>
      <Route path={completedRoutes.signUpModalItem}>
        <SignUpModalItem />
      </Route>
      {desktopOption &&
      <Route>
        <TasksGrid />
      </Route>}
    </Switch>
  );
};

function TasksPage(props) {
  const history = useHistory();
  const { t } = useTranslation();

  const handleClick = () => {
    history.push(completedRoutes.userPayment);
  };

  const {
    fetchTasks, fetchRegions, needUpdate, setNeedUpdate,
    activitiesFetchingState, regionsFetchingState, changeErrorPopupState,
    getUserBalance, isLogged, fetchUserPlaces, userPlacesFetchingState,
  } = props;

  const errorHandler = useCallback((error) => {
    if (error.status === 401) {
      history.replace(completedRoutes.signUpModalItem, { currentUrl: completedRoutes.userTasks });
    } else {
      console.error(error);
      changeErrorPopupState({ isOpen: true, error, type: 'default' });
    }
  }, [changeErrorPopupState, history]);

  useEffect(() => {
    const getPromisesByLoggedState = () => isLogged
      ? [fetchTasks(), fetchRegions(), getUserBalance(), fetchUserPlaces()]
      : [fetchTasks(), fetchRegions(), fetchUserPlaces()];
    const fetchData = () => {
      Promise.all(getPromisesByLoggedState()).catch(errorHandler)
    };
    fetchData();
  }, [fetchTasks, fetchRegions, errorHandler, getUserBalance, isLogged, fetchUserPlaces]);

  useEffect(() => {
    const fetchData = () => {
      if (needUpdate) {
        setNeedUpdate({ needUpdate: false });
        Promise.all([fetchTasks(), getUserBalance(), fetchUserPlaces()]).catch(errorHandler)
      }
    };
    fetchData();
  }, [needUpdate, fetchTasks, setNeedUpdate, errorHandler, getUserBalance, fetchUserPlaces]);

  const isLoading = regionsFetchingState === 'requesting'
    || activitiesFetchingState === 'requesting'
    || userPlacesFetchingState === 'requesting';

  return (
    <React.Fragment>
      <AppHeader />
      <Hidden only={['xs', 'sm']}>
        <Container>
          <Grid container direction="row">
            <Grid item md={2} lg={2}>
              <DesktopSideBar />
            </Grid>
            <Grid item md={10} lg={10}>
              <ContentSwitch desktopOption />
            </Grid>
          </Grid>
        </Container>
      </Hidden>
      <Hidden mdUp>
        <Container>
          <TasksGrid />
        </Container>
      </Hidden>
      <Hidden mdUp>
        <BottomButton title={t('common.userPayment')} handleClick={handleClick} green />
      </Hidden>
      <Hidden mdUp>
        <ContentSwitch />
      </Hidden>
      <Hidden only={['xs', 'sm']}>
        <CustomBackdrop open={isLoading} />
      </Hidden>
    </React.Fragment>
  );
}

const mapStateToProps = state => ({
  needUpdate: state.tasks.needUpdate,
  tasksFetchingState: state.tasks.fetchingState,
  regionsFetchingState: state.regions.fetchingState,
  activitiesFetchingState: state.activities.fetchingState,
  userPlacesFetchingState: state.places.userPlacesFetchingState,
  isLogged: state.auth.isLogged,
});

const actionCreators = {
  fetchTasks: tasksActions.fetchTasks,
  fetchRegions: regionsActions.fetchRegions,
  fetchUserPlaces: placesActions.fetchUserPlaces,
  setNeedUpdate: tasksActions.setNeedUpdate,
  changeErrorPopupState: changeErrorPopupState,
  getUserBalance: userActions.getUserBalance,
};

export default connect(mapStateToProps, actionCreators)(TasksPage);
